<script setup lang="ts">
import {
  loadYandexSmartCaptcha,
  type SmartCaptchaApi,
  type SmartCaptchaWidgetId,
} from "~/shared/lib/yandex-smartcaptcha";

const runtimeConfig = useRuntimeConfig();
const isEnabled = String(runtimeConfig.public.yandexSmartCaptchaEnabled) === "true";
const siteKey = String(
  runtimeConfig.public.yandexSmartCaptchaClientKey ?? "",
).trim();
const container = ref<HTMLElement | null>(null);

let api: SmartCaptchaApi | null = null;
let widgetId: SmartCaptchaWidgetId | null = null;
let initialization: Promise<void> | null = null;
let pending:
  | {
      resolve: (token: string) => void;
      reject: (error: Error) => void;
      timeoutId: ReturnType<typeof setTimeout>;
    }
  | null = null;

function finish(token: string) {
  if (!pending || !token) return;

  clearTimeout(pending.timeoutId);
  pending.resolve(token);
  pending = null;
}

async function initialize() {
  if (!isEnabled || widgetId !== null) return;
  if (!siteKey) {
    throw new Error(
      "SmartCaptcha включена, но публичный ключ не настроен.",
    );
  }
  if (initialization) return initialization;

  initialization = (async () => {
    api = await loadYandexSmartCaptcha();
    if (!container.value) {
      throw new Error("Не удалось инициализировать SmartCaptcha.");
    }

    widgetId = api.render(container.value, {
      sitekey: siteKey,
      invisible: true,
      hl: "ru",
      shieldPosition: "bottom-left",
      callback: finish,
    });
  })().catch((error) => {
    initialization = null;
    throw error;
  });

  return initialization;
}

async function execute() {
  if (!isEnabled) return "";

  await initialize();
  if (!api || widgetId === null) {
    throw new Error("Не удалось запустить SmartCaptcha.");
  }
  if (pending) {
    throw new Error("Проверка SmartCaptcha уже выполняется.");
  }

  api.reset(widgetId);

  return new Promise<string>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      pending = null;
      api?.reset(widgetId ?? undefined);
      reject(new Error("Время проверки SmartCaptcha истекло."));
    }, 120_000);

    pending = { resolve, reject, timeoutId };
    api?.execute(widgetId ?? undefined);
  });
}

onMounted(() => {
  if (isEnabled) void initialize().catch(() => undefined);
});

onBeforeUnmount(() => {
  if (pending) {
    clearTimeout(pending.timeoutId);
    pending.reject(new Error("Проверка SmartCaptcha отменена."));
    pending = null;
  }
  if (api && widgetId !== null) api.destroy(widgetId);
});

defineExpose({ execute });
</script>

<template>
  <div v-if="isEnabled" ref="container" />
</template>
